import React, { useState, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { ADD_PALLET_TYPE_MUTATION, UPDATE_PALLET_TYPE_MUTATION } from "../../../constants/graphql";
import { FETCH_PROJECT_QUERY } from "../../../constants/graphql/queries";
import PalletTypeModal from "../../../views/settings/Miscellaneous/PalletTypeModal";
import ConfirmDeleteModal from "../../images/ConfirmDeleteModal";
import { useProject } from "../ProjectProvider";
import { getSortedPalletTypes } from "../../../utils/palletHelpers";

export default function ProjectPalletTypesDialog({ onClose, open }) {
  const { project } = useProject();
  const { notifySuccess, notifyError } = useNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const localPalletTypes = useMemo(() => {
    return getSortedPalletTypes(project?.palletTypes || []);
  }, [project?.palletTypes]);

  const [addPalletTypeMutation] = useMutation(ADD_PALLET_TYPE_MUTATION);
  const [updatePalletTypeMutation] = useMutation(UPDATE_PALLET_TYPE_MUTATION);

  const handleSavePalletType = name => {
    const existingInactive = localPalletTypes.find(
      pt => (pt?.name || "").toLowerCase() === (name || "").toLowerCase() && !pt.isActive
    );

    if (existingInactive) {
      updatePalletTypeMutation({
        variables: {
          projectId: project.id,
          palletTypeId: existingInactive.id,
          isActive: true,
        },
        refetchQueries: [
          {
            query: FETCH_PROJECT_QUERY,
            variables: { id: project.id },
          },
        ],
        onSuccess: () => {
          notifySuccess(`Pallet type "${existingInactive.name}" restored successfully`);
          setModalOpen(false);
        },
        onFailure: error => {
          notifyError(error);
        },
      });
      return;
    }

    addPalletTypeMutation({
      variables: {
        projectId: project.id,
        name,
      },
      refetchQueries: [
        {
          query: FETCH_PROJECT_QUERY,
          variables: { id: project.id },
        },
      ],
      onSuccess: () => {
        notifySuccess(`Pallet type "${name}" added successfully`);
        setModalOpen(false);
      },
      onFailure: error => {
        notifyError(error);
      },
    });
  };

  const handleRemovePalletType = () => {
    if (!deleteId) return;

    const pt = localPalletTypes.find(p => p.id === deleteId);
    if (!pt) return;

    updatePalletTypeMutation({
      variables: {
        projectId: project.id,
        palletTypeId: deleteId,
        isActive: false,
      },
      refetchQueries: [
        {
          query: FETCH_PROJECT_QUERY,
          variables: { id: project.id },
        },
      ],
      onSuccess: () => {
        notifySuccess(`Pallet type "${pt.name}" removed successfully`);
        setDeleteId(null);
      },
      onFailure: error => {
        notifyError(error);
        setDeleteId(null);
      },
    });
  };

  const deletedType = localPalletTypes.find(pt => pt.id === deleteId);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: "600px",
            maxWidth: "90vw",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography color="textSecondary" fontSize={24} fontWeight={500} align="center">
            Edit Pallet Types
          </Typography>
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            color="textPrimary"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column", pt: 2 }}
        >
          <Box mb={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
              Add Pallet Type
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {localPalletTypes
                    .filter(pt => pt.isActive)
                    .map(pt => (
                      <TableRow key={pt.id}>
                        <TableCell component="th" scope="row">
                          <Typography color="text.primary">{pt.name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => setDeleteId(pt.id)}
                            title="Remove"
                            size="small"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {modalOpen && (
        <PalletTypeModal onClose={() => setModalOpen(false)} onSave={handleSavePalletType} />
      )}

      {deleteId && (
        <ConfirmDeleteModal
          title="Remove Pallet Type"
          descr={`Are you sure you want to remove the "${deletedType?.name}" pallet type?`}
          onClose={() => setDeleteId(null)}
          onDelete={handleRemovePalletType}
          applyLabel="Remove"
        />
      )}
    </>
  );
}
